import type { FC } from 'react';
import { useMemo, useState } from 'react';
import { Filter, Settings2 } from 'lucide-react';
import {
  isShortlinkClickIdStructure,
  isShortlinkDomainScheme,
  type ShortlinkClickIdStructure,
  type ShortlinkDomainScheme,
} from '@trafficpulse/shared';

import { useToast } from '@/providers/ToastProvider';
import { useDefaultCampaign } from '@/features/smswiser-campaigns/hooks/useDefaultCampaign';

import { Button } from '@/components/ui';
import { DateTimePicker } from '@/components/ui';
import { Input } from '@/components/ui';
import { SearchableSelect } from '@/components/ui';
import { DropOptionsSection, StaticTagsPickerSection } from '@/features/smswiser-campaigns/components/drop-modals';
import {
  LeadsListTags,
  NO_TAGS_VALUE,
  TagFilterDropdown,
} from '@/features/smswiser-campaigns/components/shared';
import {
  scheduleLocalToUtcIso,
  utcIsoToScheduleLocal,
} from '@/features/smswiser-campaigns/utils/scheduleTimezone';

import { SectionHeader } from '../form/SectionHeader';

interface ConfigurationColumnProps {
  titleBase: string;
  campaigns: Array<{ id: number; name: string }>;
  campaignId: number;
  onCampaignChange: (id: number) => void;
  /** When set, enables Save as default / Apply default for campaign. */
  countryKey?: string;
  gateways: { id: number; name: string; pricePerSms?: number | null }[];
  tagsByGatewayId?: Record<string, string[]>;
  tagColors?: Record<string, string>;
  tlds: { value: string; displayName: string }[];
  routeId: number;
  setRouteId: (v: number) => void;
  tldValue: string;
  setTldValue: (v: string) => void;
  amount: number | '';
  setAmount: (v: number | '') => void;
  executeAfter: string;
  setExecuteAfter: (v: string) => void;
  estimatedCost: string;
  isBusy: boolean;
  forceSend: boolean;
  setForceSend: (v: boolean) => void;
  sendInBatches: boolean;
  setSendInBatches: (v: boolean) => void;
  batchAmount: number | undefined;
  setBatchAmount: (v: number | undefined) => void;
  batchDelay: number | undefined;
  setBatchDelay: (v: number | undefined) => void;
  aiOptimizationEnabled: boolean;
  setAiOptimizationEnabled: (v: boolean) => void;
  aiOptimizationType: 'ROI' | 'CTR' | '';
  setAiOptimizationType: (v: 'ROI' | 'CTR') => void;
  aiOptimizationBatchAmount: number | '';
  setAiOptimizationBatchAmount: (v: number | '') => void;
  aiOptimizationMinimalValuePercent: number | '';
  setAiOptimizationMinimalValuePercent: (v: number | '') => void;
  aiOptimizationSendUntil: string;
  setAiOptimizationSendUntil: (v: string) => void;
  useOnlyNewDomains: boolean;
  setUseOnlyNewDomains: (v: boolean) => void;
  clickIdStructure: ShortlinkClickIdStructure;
  setClickIdStructure: (v: ShortlinkClickIdStructure) => void;
  domainScheme: ShortlinkDomainScheme;
  setDomainScheme: (v: ShortlinkDomainScheme) => void;
  staticTagId: string | null;
  setStaticTagId: (v: string | null) => void;
}

export const ConfigurationColumn: FC<ConfigurationColumnProps> = ({
  titleBase,
  campaigns,
  campaignId,
  onCampaignChange,
  gateways,
  tagsByGatewayId,
  tagColors,
  tlds,
  routeId,
  setRouteId,
  tldValue,
  setTldValue,
  amount,
  setAmount,
  executeAfter,
  setExecuteAfter,
  estimatedCost,
  isBusy,
  forceSend,
  setForceSend,
  sendInBatches,
  setSendInBatches,
  batchAmount,
  setBatchAmount,
  batchDelay,
  setBatchDelay,
  aiOptimizationEnabled,
  setAiOptimizationEnabled,
  aiOptimizationType,
  setAiOptimizationType,
  aiOptimizationBatchAmount,
  setAiOptimizationBatchAmount,
  aiOptimizationMinimalValuePercent,
  setAiOptimizationMinimalValuePercent,
  aiOptimizationSendUntil,
  setAiOptimizationSendUntil,
  useOnlyNewDomains,
  setUseOnlyNewDomains,
  clickIdStructure,
  setClickIdStructure,
  domainScheme,
  setDomainScheme,
  staticTagId,
  setStaticTagId,
  countryKey,
}) => {
  const [gatewayTagFilter, setGatewayTagFilter] = useState<string>('');
  const { defaultCampaignId, setDefaultCampaign } = useDefaultCampaign(countryKey ?? '');
  const { success: toastSuccess } = useToast();

  const handleSaveAsDefault = () => {
    if (campaignId <= 0) return;
    setDefaultCampaign(campaignId);
    const name = campaigns.find((c) => c.id === campaignId)?.name ?? 'Campaign';
    toastSuccess(`${name} saved as default`);
  };
  const campaignOptions = [
    { value: 0, label: 'Select campaign' },
    ...campaigns.map((c) => ({ value: c.id, label: c.name })),
  ];

  const getTagsForRouteId = (rid: number): string[] | undefined => {
    if (!tagsByGatewayId) return undefined;
    const direct = tagsByGatewayId[String(rid)];
    if (direct) return direct;
    const matchingKey = Object.keys(tagsByGatewayId).find(
      (k) => k.endsWith(`_${rid}`) || k === String(rid),
    );
    return matchingKey ? tagsByGatewayId[matchingKey] : undefined;
  };

  const availableGatewayTags = useMemo(() => {
    if (!tagsByGatewayId) return [];
    const set = new Set<string>();
    for (const tags of Object.values(tagsByGatewayId)) {
      for (const t of tags ?? []) if (t && String(t).trim()) set.add(String(t).trim());
    }
    return [...set].sort();
  }, [tagsByGatewayId]);

  const gatewayOptions = useMemo(() => {
    const options = gateways.map((g) => {
      const base = `${g.name}${g.pricePerSms != null ? ` ($${Number(g.pricePerSms).toFixed(3)}/SMS)` : ''}`;
      const tags = getTagsForRouteId(g.id);
      const label = tags?.length ? `${base} ${tags.map((t) => `[${t}]`).join(' ')}` : base;
      const optionContent =
        tags?.length ? (
          <span className="flex flex-wrap items-center gap-1.5">
            <span>{base}</span>
            <LeadsListTags performanceTags={tags} tagColors={tagColors} compact />
          </span>
        ) : undefined;
      // Closed trigger = one h-10 line; the wrapping card above grew a second
      // chip row there. Name truncates first, chips clip at the edge.
      const triggerContent =
        tags?.length ? (
          <span className="flex min-w-0 flex-1 items-center gap-1.5 overflow-hidden">
            <span className="min-w-0 flex-1 truncate">{base}</span>
            {/* One chip + the +N pill: the NAME identifies the pick — full
                chips live in the dropdown and in the +N hover. */}
            <LeadsListTags performanceTags={tags} tagColors={tagColors} compact maxVisible={1} />
          </span>
        ) : undefined;
      return { value: g.id, label, optionContent, triggerContent };
    });
    if (!gatewayTagFilter || !tagsByGatewayId) return options;
    if (gatewayTagFilter === NO_TAGS_VALUE) {
      return options.filter((o) => !getTagsForRouteId(o.value)?.length);
    }
    const q = gatewayTagFilter.toLowerCase();
    return options.filter((o) =>
      getTagsForRouteId(o.value)?.some((t) => t.toLowerCase() === q),
    );
  }, [gateways, gatewayTagFilter, tagsByGatewayId, tagColors]);

  return (
    <div className="flex w-full shrink-0 flex-col border-b border-border xl:w-[300px] xl:min-h-0 xl:border-b-0 xl:border-r">
      <SectionHeader icon={Settings2} title="Configuration" subtitle="Route & Schedule" />

      {/* gap-2 / p-2.5 rather than 3: the rail is 300px wide, so these fields
       *  are always a single tall stack and every 4px of gap is 28px of column.
       *  Tightening them is what lets the whole config fit on a laptop without
       *  a scrollbar. */}
      <div className="config-fields-container flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto p-2.5">
        <div className="rounded-md border border-border/60 bg-muted/20 px-3 py-2">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Draft Name Base</span>
          <div className="mt-0.5 break-words text-xs font-medium text-foreground">{titleBase}</div>
        </div>

        <div className="config-fields">
          <div className="space-y-1.5">
            <div className="flex items-center justify-between gap-1">
              <label className="text-xs font-medium text-foreground">Campaign</label>
              {countryKey && (
                <span className="flex items-center gap-1.5 text-[10px]">
                  {campaignId > 0 ? (
                    <button
                      type="button"
                      className="text-muted-foreground hover:text-foreground"
                      onClick={handleSaveAsDefault}
                    >
                      Save as default
                    </button>
                  ) : defaultCampaignId != null && campaigns.some((c) => c.id === defaultCampaignId) ? (
                    <button
                      type="button"
                      className="text-muted-foreground hover:text-foreground"
                      onClick={() => onCampaignChange(defaultCampaignId)}
                    >
                      Apply default
                    </button>
                  ) : null}
                </span>
              )}
            </div>
            <SearchableSelect<number>
              options={campaignOptions}
              value={campaignId}
              onChange={onCampaignChange}
              placeholder="You must select a campaign"
              searchPlaceholder="Search campaigns…"
              disabled={isBusy}
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-foreground">Gateway Route</label>
            <SearchableSelect<number>
              options={[
                { value: 0, label: 'Select gateway' },
                ...gatewayOptions,
              ]}
              value={routeId}
              onChange={setRouteId}
              placeholder="Select gateway"
              searchPlaceholder="Search gateways..."
              disabled={isBusy}
              headerSlot={
                availableGatewayTags.length > 0 ? (
                  <div className="flex shrink-0 items-center gap-2">
                    <Filter className="h-3.5 w-3.5 text-muted-foreground" />
                    <TagFilterDropdown
                      value={gatewayTagFilter}
                      options={availableGatewayTags}
                      tagColors={tagColors}
                      displayLabel={
                        gatewayTagFilter === ''
                          ? 'All tags'
                          : gatewayTagFilter === NO_TAGS_VALUE
                            ? 'No tags'
                            : gatewayTagFilter
                      }
                      onSelect={setGatewayTagFilter}
                      placeholder="All tags"
                      compact
                    />
                  </div>
                ) : undefined
              }
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-foreground">Shortlink TLD</label>
            <SearchableSelect<string>
              options={[
                { value: '', label: 'Select TLD' },
                ...tlds.map((t) => ({ value: t.value, label: t.displayName })),
              ]}
              value={tldValue || ''}
              onChange={(v) => setTldValue(v || '')}
              placeholder="Select TLD"
              searchPlaceholder="Search TLDs..."
              disabled={isBusy}
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-foreground">Amount per list</label>
            <Input
              type="number"
              min={1}
              value={amount === '' ? '' : amount}
              onChange={(e) => setAmount(e.target.value === '' ? '' : Number(e.target.value))}
              disabled={isBusy}
              error={
                amount === '' || (typeof amount === 'number' && amount < 1) ? 'Min 1 required' : undefined
              }
            />
            <div className="flex items-center gap-1.5 text-xs">
              <span className="text-muted-foreground">Cost:</span>
              <span className="font-bold text-destructive">${estimatedCost}</span>
            </div>
          </div>

          <div className="space-y-1.5 config-field-full">
            <label className="flex items-center justify-between text-xs font-medium text-foreground">
              Schedule Drop
              <span className="flex items-center gap-1 text-[9px] font-normal text-muted-foreground">
                <span>(optional)</span>
                <span>·</span>
                <span>(Danish time)</span>
              </span>
            </label>
            <div className="flex items-center gap-2">
              <DateTimePicker
                value={executeAfter ? utcIsoToScheduleLocal(executeAfter) : ''}
                onChange={(v) => setExecuteAfter(v ? scheduleLocalToUtcIso(v) : '')}
                placeholder="dd/mm/yyyy, --:--"
                disabled={isBusy}
                minDateToday
                ariaLabel="Schedule drop date and time"
                className="min-w-0 flex-1"
              />
              {executeAfter && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="shrink-0 text-xs text-muted-foreground hover:text-foreground"
                  onClick={() => setExecuteAfter('')}
                  disabled={isBusy}
                >
                  Clear
                </Button>
              )}
            </div>
          </div>

          <div className="config-field-full border-t border-border/60 pt-3">
            <DropOptionsSection
              compact
              enableBatchesAndAi
              amount={amount}
              forceSend={forceSend}
              setForceSend={(v) => { if (v !== 'unchanged') setForceSend(v); }}
              sendInBatches={sendInBatches}
              setSendInBatches={(v) => { if (v !== 'unchanged') setSendInBatches(v); }}
              batchAmount={batchAmount}
              setBatchAmount={setBatchAmount}
              batchDelay={batchDelay}
              setBatchDelay={setBatchDelay}
              aiOptimizationEnabled={aiOptimizationEnabled}
              setAiOptimizationEnabled={setAiOptimizationEnabled}
              aiOptimizationType={aiOptimizationType}
              setAiOptimizationType={setAiOptimizationType}
              aiOptimizationBatchAmount={aiOptimizationBatchAmount}
              setAiOptimizationBatchAmount={setAiOptimizationBatchAmount}
              aiOptimizationMinimalValuePercent={aiOptimizationMinimalValuePercent}
              setAiOptimizationMinimalValuePercent={setAiOptimizationMinimalValuePercent}
              aiOptimizationSendUntil={aiOptimizationSendUntil}
              setAiOptimizationSendUntil={setAiOptimizationSendUntil}
              useOnlyNewDomains={useOnlyNewDomains}
              setUseOnlyNewDomains={(v) => { if (v !== 'unchanged') setUseOnlyNewDomains(v); }}
              shortlink={{
                clickIdStructure,
                setClickIdStructure: (v) => { if (isShortlinkClickIdStructure(v)) setClickIdStructure(v); },
                domainScheme,
                setDomainScheme: (v) => { if (isShortlinkDomainScheme(v)) setDomainScheme(v); },
              }}
              isBusy={isBusy}
            />
          </div>
          <div className="config-field-full border-t border-border/60 pt-3">
            <StaticTagsPickerSection
              mode="single"
              selectedId={staticTagId}
              onChange={setStaticTagId}
              disabled={isBusy}
              compact
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfigurationColumn;
